/**
 * Minimap (Sirus 3.3.5a) — R232q21 Phase 2.
 *
 * WoW 3.3.5a / Sirus-style круглая мини-карта зоны. Отображает текущую
 * зону игрока (background-tile, не глобальный мир), стрелку игрока в
 * центре, координаты, время сервера и переключатели трекинга.
 *
 * Поведение «зона-первая» (по запросу юзера 2026-05-07):
 *   • Фон canvas — изображение текущей зоны `/zones/<zoneId>/minimap.png`.
 *     Если его нет — рисуем процедурный пергаментный radial-gradient.
 *   • Стрелка игрока неподвижна в центре, фон сдвигается в обратную сторону
 *     при движении (классический WoW-эффект «мир крутится вокруг тебя»).
 *   • Зум — это CSS-scale фоновой подложки, 1× / 2× / 3× / 4×.
 *
 * Что НЕ делает (deferred):
 *   • POI-маркеры (квестодатели, руды, рыба) — требуют DBC + поллинг сети,
 *     это Phase 3. Чекбоксы трекинга сохраняют состояние локально, но
 *     отрисовку точек пока не запускают.
 *   • Анимированная погода — иконка статичная.
 *   • Календарь / Почта — заглушки `console.log`.
 *
 * Старый `components/Minimap.tsx` остаётся в репо для World.tsx, замена
 * этим компонентом будет отдельным коммитом.
 */

import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useNet, useSelfPlayer } from '../../state/net-store.js';
import { useQuestPoi } from '../../state/quest-poi-store.js';
import { zoneAt, zoneById } from '../../data/zones.js';

// ── Геометрия ────────────────────────────────────────────────────────────
const FRAME_PX = 200;      // внешний gold-trim
const CANVAS_PX = 144;     // R232q80: было 192, ужал до 144 чтобы соответствовать стандартному размеру минимапы 3.3.5а
const HALF = CANVAS_PX / 2;
/** Метров мира на 1 px минимапа при зуме 1×. Подобрано под средний размер
 *  суб-зоны (~400×400 м) — на zoom 1× в круг помещается ~480 м. */
const METRES_PER_PX = 2.5;

// ── Типы трекинга ────────────────────────────────────────────────────────
type TrackKey = 'herbs' | 'ore' | 'fish' | 'questGivers' | 'treasure';

const TRACK_LABELS: Record<TrackKey, string> = {
  herbs: 'Травы',
  ore: 'Руда',
  fish: 'Рыба',
  questGivers: 'Квестодатели',
  treasure: 'Сокровища',
};

// ── Кардинальные направления ─────────────────────────────────────────────
/**
 * Возвращает букву С/Ю/В/З для стороны, которая сейчас «вверху» круга
 * с учётом heading игрока. heading в радианах: 0 — север (z−), π/2 — восток.
 */
function cardinalForHeading(heading: number): 'С' | 'В' | 'Ю' | 'З' {
  const tau = Math.PI * 2;
  const norm = ((heading % tau) + tau) % tau;
  // Нормализованные октанты: 0..π/4 → С, π/4..3π/4 → В, и т.д.
  if (norm < Math.PI * 0.25 || norm >= Math.PI * 1.75) return 'С';
  if (norm < Math.PI * 0.75) return 'В';
  if (norm < Math.PI * 1.25) return 'Ю';
  return 'З';
}

// ── Хук: 1Hz «тикер» для clock + позиции (без перерисовки на каждый кадр) ──
function useSecondTicker(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

/** Форматирует HH:MM в локальном часовом поясе. */
function formatClock(ms: number): string {
  const d = new Date(ms);
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

// ── Компонент ────────────────────────────────────────────────────────────
function MinimapImpl() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const bgImgRef = useRef<HTMLImageElement | null>(null);
  const bgImgZoneRef = useRef<string | null>(null);
  const bgImgLoadedRef = useRef(false);

  const [zoom, setZoom] = useState<number>(1);
  const [tracking, setTracking] = useState<Record<TrackKey, boolean>>({
    herbs: false,
    ore: false,
    fish: false,
    questGivers: true,
    treasure: false,
  });
  const [trackingOpen, setTrackingOpen] = useState(false);

  const nowMs = useSecondTicker();

  // Подписка на «тонкие» поля игрока — без полного `players` объекта,
  // чтобы не дёргать ререндер всего компонента на каждое движение мобов.
  // R232q86zdb (Wave 2 #1): `useSelfPlayer()` — canonical narrow selector.
  // Note: внутренний draw-loop ниже использует `useNet.getState()`
  // (синхронное чтение, без подписки) — это полностью корректно: canvas
  // перерисовывается через requestAnimationFrame, не через React render.
  const selfId = useNet((s) => s.selfId);
  const selfRow = useSelfPlayer();
  const self = selfRow ?? null;

  const zone = useMemo(() => {
    if (!self) return null;
    if (self.zoneId) {
      const byId = zoneById(self.zoneId);
      if (byId) return byId;
    }
    return zoneAt(self.pos.x, self.pos.z);
  }, [self]);

  const zoneName = zone?.name.ru ?? 'Неизвестно';
  const zoneSubName: string | null = null; // sub-zone отдельным запросом — Phase 3.

  // Префетч/смена фоновой плитки зоны при смене zoneId.
  useEffect(() => {
    const zid = zone?.id ?? null;
    if (zid === bgImgZoneRef.current) return;
    bgImgZoneRef.current = zid;
    bgImgLoadedRef.current = false;
    if (!zid) {
      bgImgRef.current = null;
      return;
    }
    // R232q86zbb: попробовать список paths по приоритету. Сначала
    // существующие /maps/zone-<id>.{png,jpg} (Wave 5 generated 86
    // tile assets), затем legacy /zones/<id>/minimap.png, потом
    // continent-level world map для tidal-spawn / anchorless-spawn /
    // stormveil-crossroads, наконец aiwari-world-map.png как ultimate
    // fallback. Image onerror рекурсивно идёт к next path.
    const candidates = [
      `${import.meta.env.BASE_URL}maps/zone-${zid}.png`,
      `${import.meta.env.BASE_URL}maps/zone-${zid}.jpg`,
      `${import.meta.env.BASE_URL}zones/${zid}/minimap.png`,
      // Continent / midline aliases.
      `${import.meta.env.BASE_URL}maps/zone-stormveil.png`,
      `${import.meta.env.BASE_URL}maps/aiwari-world-map.png`,
    ];
    let idx = 0;
    const tryNext = () => {
      if (idx >= candidates.length || bgImgZoneRef.current !== zid) {
        if (bgImgZoneRef.current === zid) {
          bgImgRef.current = null;
          bgImgLoadedRef.current = false;
        }
        return;
      }
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => {
        if (bgImgZoneRef.current === zid) {
          bgImgRef.current = img;
          bgImgLoadedRef.current = true;
        }
      };
      img.onerror = () => {
        idx++;
        tryNext();
      };
      img.src = candidates[idx]!;
    };
    tryNext();
  }, [zone?.id]);

  // ── Главный draw-loop ────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let raf = 0;

    const draw = () => {
      const net = useNet.getState();
      const me = net.selfId ? net.players[net.selfId] : null;
      const px = me?.pos.x ?? 0;
      const pz = me?.pos.z ?? 0;
      const heading = me?.heading ?? 0;

      ctx.clearRect(0, 0, CANVAS_PX, CANVAS_PX);

      // Круглый clip — содержимое не должно вылезать за gold-trim.
      ctx.save();
      ctx.beginPath();
      ctx.arc(HALF, HALF, HALF - 1, 0, Math.PI * 2);
      ctx.clip();

      const img = bgImgRef.current;
      const z = zone;
      if (img && bgImgLoadedRef.current && z) {
        // Привязка world-bbox зоны → image-pixels. Игрок в центре, фон
        // сдвигается противоположно его смещению относительно центра зоны.
        const zw = z.bounds.maxX - z.bounds.minX;
        const zh = z.bounds.maxZ - z.bounds.minZ;
        // Доля игрока внутри bbox (0..1).
        const fx = (px - z.bounds.minX) / Math.max(1, zw);
        const fz = (pz - z.bounds.minZ) / Math.max(1, zh);
        // Размер «оригинальной» проекции тайла на canvas: при zoom=1
        // ширина окна = CANVAS_PX, тайл рисуется так, чтобы 1 zone-bbox
        // покрывал диаметр × ratio (≈ 4×). Чем больше zoom, тем меньше
        // тайла видно (больше детализация).
        const baseTilePx = CANVAS_PX * 4; // 4-крат оригинала на zoom 1×
        const tilePx = baseTilePx * zoom;
        const dx = HALF - fx * tilePx;
        const dy = HALF - fz * tilePx;
        ctx.drawImage(img, dx, dy, tilePx, tilePx);
      } else {
        // Procedural parchment fallback — radial gradient + лёгкий шум.
        const grad = ctx.createRadialGradient(HALF, HALF, 8, HALF, HALF, HALF);
        grad.addColorStop(0, '#3a2e1e');
        grad.addColorStop(0.55, '#2a2114');
        grad.addColorStop(1, '#0f0a05');
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, CANVAS_PX, CANVAS_PX);
        // «Бумажный» шум — неперсистентный, но дешёвый.
        for (let i = 0; i < 80; i++) {
          const sx = Math.random() * CANVAS_PX;
          const sy = Math.random() * CANVAS_PX;
          ctx.fillStyle = Math.random() < 0.5
            ? 'rgba(255,220,150,0.04)'
            : 'rgba(0,0,0,0.10)';
          ctx.fillRect(sx, sy, 1, 1);
        }
        // Лёгкий компас-крест чтобы не выглядел совсем пустым.
        ctx.strokeStyle = 'rgba(232,199,123,0.08)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(HALF, 12); ctx.lineTo(HALF, CANVAS_PX - 12);
        ctx.moveTo(12, HALF); ctx.lineTo(CANVAS_PX - 12, HALF);
        ctx.stroke();
      }

      // R232q22 Phase 3: Quest POI («!»/«?»). Рисуем внутри clip так, чтобы
      // маркеры обрезались по кругу мини-карты. Координаты POI считаем тем же
      // mapping'ом (world → tile → canvas), что и для фона.
      if (z) {
        const zw = z.bounds.maxX - z.bounds.minX;
        const zh = z.bounds.maxZ - z.bounds.minZ;
        const baseTilePx = CANVAS_PX * 4;
        const tilePx = baseTilePx * zoom;
        const fxMe = (px - z.bounds.minX) / Math.max(1, zw);
        const fzMe = (pz - z.bounds.minZ) / Math.max(1, zh);
        const dx = HALF - fxMe * tilePx;
        const dy = HALF - fzMe * tilePx;
        const pulse = 0.55 + 0.45 * (0.5 + 0.5 * Math.sin(performance.now() / 380));

        // R232q86zb2: server-replicated NPC quest markers.
        // Combines local static-catalogue POIs (legacy) + server NPC `questMarker`.
        // Server marker '!' = available, '?' = ready turn-in, '!?' = both.
        // Read NPCs from `net` snapshot (already captured at top of draw()).
        const replicatedPois: Array<{ x: number; z: number; zoneId: string; kind: 'available' | 'turnin' | 'in-progress'; npcId: string }> = [];
        let acceptedGiverIds: Set<string> = new Set();
        try {
          const acc = JSON.parse(localStorage.getItem('aiwari-quest-state:accepted') ?? '[]') as Array<{ giver?: string }>;
          acceptedGiverIds = new Set(acc.map((a) => a.giver).filter(Boolean) as string[]);
        } catch { /* */ }
        for (const id of Object.keys(net.npcs)) {
          const npc = net.npcs[id];
          if (!npc) continue;
          if (npc.npcType !== 'questGiver') continue;
          const marker = npc.questMarker ?? '';
          let kind: 'available' | 'turnin' | 'in-progress' | null = null;
          if (marker.includes('?')) kind = 'turnin';
          else if (marker.includes('!')) kind = 'available';
          else if (acceptedGiverIds.has(id)) kind = 'in-progress';
          if (!kind) continue;
          replicatedPois.push({ x: npc.pos.x, z: npc.pos.z, zoneId: npc.zoneId, kind, npcId: id });
        }
        const pois = [...useQuestPoi.getState().pois, ...replicatedPois];
        for (const p of pois) {
          // Replicated POIs use server zoneId; legacy POIs use local zoneAt() id.
          // If they don't match the current zone, skip (keeps minimap-locality).
          if (p.zoneId !== z.id) continue;
          const fx = (p.x - z.bounds.minX) / Math.max(1, zw);
          const fz = (p.z - z.bounds.minZ) / Math.max(1, zh);
          const sx = dx + fx * tilePx;
          const sy = dy + fz * tilePx;
          // Cull точек далеко за кругом (запас 12 px на глиф).
          const dxC = sx - HALF;
          const dyC = sy - HALF;
          if (dxC * dxC + dyC * dyC > (HALF + 12) * (HALF + 12)) continue;

          if (p.kind === 'available') {
            // Жёлтый «!» — заполненный треугольник вверх + точка под ним.
            ctx.save();
            ctx.fillStyle = `rgba(250,179,0,${0.92 * pulse})`;
            ctx.strokeStyle = '#0F0900';
            ctx.lineWidth = 1.2;
            ctx.beginPath();
            ctx.moveTo(sx, sy - 6);
            ctx.lineTo(sx + 5, sy + 3);
            ctx.lineTo(sx - 5, sy + 3);
            ctx.closePath();
            ctx.fill();
            ctx.stroke();
            ctx.beginPath();
            ctx.arc(sx, sy + 6, 1.4, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
          } else if (p.kind === 'turnin') {
            // Жёлтый «?» в круге.
            ctx.save();
            ctx.fillStyle = 'rgba(11,9,7,0.85)';
            ctx.strokeStyle = `rgba(250,179,0,${0.95 * pulse})`;
            ctx.lineWidth = 1.4;
            ctx.beginPath();
            ctx.arc(sx, sy, 5.5, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
            ctx.fillStyle = '#FAB300';
            ctx.font = 'bold 9px var(--aiwari-font-display), serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('?', sx, sy + 0.5);
            ctx.restore();
          } else {
            // Серый «?» — квест в процессе.
            ctx.save();
            ctx.fillStyle = 'rgba(11,9,7,0.7)';
            ctx.strokeStyle = 'rgba(180,180,180,0.85)';
            ctx.lineWidth = 1.2;
            ctx.beginPath();
            ctx.arc(sx, sy, 5, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
            ctx.fillStyle = '#C8C8C8';
            ctx.font = 'bold 8px var(--aiwari-font-display), serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('?', sx, sy + 0.5);
            ctx.restore();
          }
        }
      }

      ctx.restore(); // снять clip

      // Кольцевой бордер внутри (декоративный, без 9-slice).
      ctx.strokeStyle = 'rgba(232,199,123,0.35)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(HALF, HALF, HALF - 1, 0, Math.PI * 2);
      ctx.stroke();

      // Стрелка игрока — золотой треугольник в центре, повернут по heading.
      // heading=0 → север (треугольник носом вверх).
      ctx.save();
      ctx.translate(HALF, HALF);
      ctx.rotate(heading);
      ctx.beginPath();
      ctx.moveTo(0, -7);
      ctx.lineTo(5, 6);
      ctx.lineTo(0, 3);
      ctx.lineTo(-5, 6);
      ctx.closePath();
      ctx.fillStyle = '#FAB300';
      ctx.strokeStyle = '#0F0900';
      ctx.lineWidth = 1.2;
      ctx.fill();
      ctx.stroke();
      ctx.restore();

      // Тонкое золотое свечение под стрелкой — узнаваемость.
      ctx.fillStyle = 'rgba(250,179,0,0.18)';
      ctx.beginPath();
      ctx.arc(HALF, HALF, 9, 0, Math.PI * 2);
      ctx.fill();

      // METRES_PER_PX используется в подписи масштаба (см. ниже).
      void METRES_PER_PX;

      raf = requestAnimationFrame(draw);
    };

    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [zone, zoom]);

  // R232q86zb6: Координаты — читаем из visual rig вместо net-store (которое
  // зависит от ненадёжного Colyseus nested Schema decode для self.pos).
  // selfRig.root.position обновляется server-snap (R232q86zb5) + локальным
  // input, поэтому всегда отражает реальную visual позицию игрока.
  // Fallback на net-store self.pos если rig ещё не загружен.
  const rigForCoords = (typeof window !== 'undefined' ? (window as { __aiwariSelfRig?: { root?: { position?: { x: number; z: number } } } }).__aiwariSelfRig : null);
  const rigPos = rigForCoords?.root?.position;
  const coordX = rigPos?.x ?? self?.pos.x ?? 0;
  const coordZ = rigPos?.z ?? self?.pos.z ?? 0;
  void nowMs; // re-render trigger 1Hz to refresh visual coords
  const cardinal = cardinalForHeading(self?.heading ?? 0);

  // R232q79: рендерим минимапу даже без коннекта — placeholder вместо
  // invisible. Юзер должен видеть HUD сразу. selfId использован для
  // условного «Подключение…» лейбла внутри.
  void selfId;

  return (
    <div className="aiwari-sirus-minimap" data-zoom={zoom}>
      {/* ── Заголовок зоны ── */}
      <div className="zone-label">
        <span className="zone-name">{zoneName}</span>
        {zoneSubName && <span className="zone-sub">{zoneSubName}</span>}
      </div>

      {/* ── Тело: круг + накладки ── */}
      <div className="ring">
        {/* Cardinal (всегда «верх» круга — буква, повёрнутая по heading) */}
        <div className="cardinal" aria-hidden>{cardinal}</div>

        {/* Tracking dropdown (top-left) */}
        <div className="tracking">
          <button
            type="button"
            className="mini-btn"
            aria-label="Трекинг"
            onClick={() => setTrackingOpen((v) => !v)}
          >
            {/* Лупа SVG */}
            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden>
              <circle cx="6" cy="6" r="4" fill="none" stroke="currentColor" strokeWidth="1.4" />
              <line x1="9.2" y1="9.2" x2="12.5" y2="12.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
          </button>
          {trackingOpen && (
            <ul className="tracking-menu" role="menu">
              {(Object.keys(TRACK_LABELS) as TrackKey[]).map((k) => (
                <li key={k}>
                  <label>
                    <input
                      type="checkbox"
                      checked={tracking[k]}
                      onChange={(e) =>
                        setTracking((prev) => ({ ...prev, [k]: e.target.checked }))
                      }
                    />
                    <span>{TRACK_LABELS[k]}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}

          {/* Календарь / Почта — placeholders */}
          <button
            type="button"
            className="mini-btn icon-btn"
            aria-label="Календарь"
            onClick={() => console.log('[Minimap] open calendar (TODO)')}
          >
            <svg width="11" height="11" viewBox="0 0 14 14" aria-hidden>
              <rect x="1.5" y="3" width="11" height="9" fill="none" stroke="currentColor" strokeWidth="1.2" />
              <line x1="1.5" y1="6" x2="12.5" y2="6" stroke="currentColor" strokeWidth="1.2" />
              <line x1="4" y1="1.5" x2="4" y2="4" stroke="currentColor" strokeWidth="1.2" />
              <line x1="10" y1="1.5" x2="10" y2="4" stroke="currentColor" strokeWidth="1.2" />
            </svg>
          </button>
          <button
            type="button"
            className="mini-btn icon-btn"
            aria-label="Почта"
            onClick={() => console.log('[Minimap] open mail (TODO)')}
          >
            <svg width="11" height="11" viewBox="0 0 14 14" aria-hidden>
              <rect x="1.5" y="3" width="11" height="8" fill="none" stroke="currentColor" strokeWidth="1.2" />
              <polyline points="1.5,3 7,8 12.5,3" fill="none" stroke="currentColor" strokeWidth="1.2" />
            </svg>
          </button>
        </div>

        {/* Zoom buttons (top-right) */}
        <div className="zoom-stack">
          <button
            type="button"
            className="mini-btn"
            aria-label="Приблизить"
            onClick={() => setZoom((z) => Math.min(4, z + 1))}
            disabled={zoom >= 4}
          >+</button>
          <button
            type="button"
            className="mini-btn"
            aria-label="Отдалить"
            onClick={() => setZoom((z) => Math.max(1, z - 1))}
            disabled={zoom <= 1}
          >−</button>
        </div>

        {/* Canvas (под всеми оверлеями) */}
        <canvas
          ref={canvasRef}
          width={CANVAS_PX}
          height={CANVAS_PX}
          className="canvas"
        />
      </div>

      {/* ── Координаты ── */}
      <div className="coords">
        {coordX.toFixed(1)}, {coordZ.toFixed(1)}
      </div>

      {/* ── Время + погода ── */}
      <div className="meta">
        <span className="clock">{formatClock(nowMs)}</span>
        <span className="weather" aria-label="Погода: ясно" title="ясно">
          {/* Простая «солнце» SVG-глифа — анимация в Phase 3. */}
          <svg width="11" height="11" viewBox="0 0 14 14" aria-hidden>
            <circle cx="7" cy="7" r="2.6" fill="currentColor" />
            <g stroke="currentColor" strokeWidth="1.2" strokeLinecap="round">
              <line x1="7" y1="0.5" x2="7" y2="2.5" />
              <line x1="7" y1="11.5" x2="7" y2="13.5" />
              <line x1="0.5" y1="7" x2="2.5" y2="7" />
              <line x1="11.5" y1="7" x2="13.5" y2="7" />
              <line x1="2.2" y1="2.2" x2="3.6" y2="3.6" />
              <line x1="10.4" y1="10.4" x2="11.8" y2="11.8" />
              <line x1="11.8" y1="2.2" x2="10.4" y2="3.6" />
              <line x1="3.6" y1="10.4" x2="2.2" y2="11.8" />
            </g>
          </svg>
        </span>
      </div>

      <style>{`
        /* R232q84: minimap rubber-sized — outer frame uses --aiwari-minimap clamp.
           Canvas keeps its 144px raster resolution but scales visually via 100%
           width/height inside the wrapper. ${FRAME_PX} legacy constant retained
           only for its draw-loop bbox math; CSS no longer references it. */
        .aiwari-sirus-minimap {
          position: fixed;
          top: var(--aiwari-padding);
          right: var(--aiwari-padding);
          z-index: 30;
          width: var(--aiwari-minimap);
          color: var(--aiwari-fg);
          font-family: var(--aiwari-font-body);
          user-select: none;
          pointer-events: auto;
        }
        .aiwari-sirus-minimap .zone-label {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1px;
          margin-bottom: var(--aiwari-gap);
          text-align: center;
        }
        .aiwari-sirus-minimap .zone-name {
          font-family: var(--aiwari-font-display);
          font-weight: 600;
          font-size: var(--aiwari-text-md);
          letter-spacing: 0.06em;
          color: var(--aiwari-gold);
          text-shadow: 0 1px 0 #000, 0 0 6px rgba(250, 179, 0, 0.35);
        }
        .aiwari-sirus-minimap .zone-sub {
          font-size: var(--aiwari-text-sm);
          color: var(--aiwari-fg-muted);
          letter-spacing: 0.03em;
        }
        .aiwari-sirus-minimap .ring {
          position: relative;
          width: var(--aiwari-minimap);
          height: var(--aiwari-minimap);
          border-radius: 50%;
          background: radial-gradient(circle at center, #161210, #0B0907);
          /* R232q86zcj: WotLK-authentic double-ring + dragon-gold border */
          border: 4px solid hsl(44 100% 32%);
          box-shadow:
            0 0 0 2px hsl(36 100% 12%),                     /* dark frame outside */
            0 0 0 5px hsl(44 100% 49% / 0.55),              /* gold filigree halo */
            0 4px 14px hsl(0 0% 0% / 0.85),
            inset 0 0 0 1px hsl(48 100% 70% / 0.45),        /* gold inner highlight */
            inset 0 0 18px hsl(36 100% 6% / 0.85),
            inset 0 2px 0 hsl(48 100% 70% / 0.18);
          padding: 4px;
          box-sizing: border-box;
        }
        .aiwari-sirus-minimap .canvas {
          display: block;
          /* Native pixel buffer is ${CANVAS_PX}×${CANVAS_PX} (set on the
             <canvas width/height> attrs). CSS scales it to fill the wrapper
             so the visible diameter follows --aiwari-minimap. */
          width: 100%;
          height: 100%;
          border-radius: 50%;
        }
        .aiwari-sirus-minimap .cardinal {
          position: absolute;
          top: 1px;
          left: 50%;
          transform: translateX(-50%);
          font-family: var(--aiwari-font-display);
          font-weight: 700;
          font-size: 11px;
          color: var(--aiwari-gold-soft);
          text-shadow: 0 0 4px #0B0907, 0 0 6px rgba(232, 199, 123, 0.45);
          letter-spacing: 0.05em;
          pointer-events: none;
        }
        .aiwari-sirus-minimap .zoom-stack {
          position: absolute;
          top: 4px;
          right: -4px;
          display: flex;
          flex-direction: column;
          gap: 2px;
          z-index: 2;
        }
        .aiwari-sirus-minimap .tracking {
          position: absolute;
          top: 4px;
          left: -4px;
          display: flex;
          flex-direction: column;
          gap: 2px;
          z-index: 2;
        }
        .aiwari-sirus-minimap .mini-btn {
          width: clamp(18px, 2.4vmin, 28px);
          height: clamp(18px, 2.4vmin, 28px);
          padding: 0;
          font-family: var(--aiwari-font-display);
          font-size: var(--aiwari-text-md);
          font-weight: 700;
          line-height: 1;
          color: var(--aiwari-gold);
          background: linear-gradient(180deg, #1a1410, #0B0907);
          border: 1px solid var(--aiwari-frame-edge);
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: border-color 80ms linear, box-shadow 80ms linear;
        }
        .aiwari-sirus-minimap .mini-btn:hover:not(:disabled) {
          border-color: var(--aiwari-gold);
          box-shadow: 0 0 6px rgba(250, 179, 0, 0.45);
        }
        .aiwari-sirus-minimap .mini-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .aiwari-sirus-minimap .mini-btn.icon-btn {
          width: 16px;
          height: 16px;
        }
        .aiwari-sirus-minimap .tracking-menu {
          position: absolute;
          top: 0;
          left: 28px;
          margin: 0;
          padding: 6px 8px;
          list-style: none;
          background: var(--aiwari-bg-card-solid);
          border: 1px solid var(--aiwari-frame-edge);
          border-radius: 3px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.7);
          font-size: 11px;
          white-space: nowrap;
          z-index: 3;
        }
        .aiwari-sirus-minimap .tracking-menu li {
          padding: 2px 0;
        }
        .aiwari-sirus-minimap .tracking-menu label {
          display: flex;
          align-items: center;
          gap: 6px;
          cursor: pointer;
          color: var(--aiwari-fg);
        }
        .aiwari-sirus-minimap .tracking-menu input[type='checkbox'] {
          accent-color: var(--aiwari-gold);
        }
        .aiwari-sirus-minimap .coords {
          margin-top: var(--aiwari-gap);
          text-align: center;
          font-family: var(--aiwari-font-mono);
          font-size: var(--aiwari-text-sm);
          color: var(--aiwari-fg);
          text-shadow: 0 1px 0 #000;
        }
        .aiwari-sirus-minimap .meta {
          margin-top: 1px;
          display: flex;
          justify-content: center;
          align-items: center;
          gap: var(--aiwari-gap);
          font-size: var(--aiwari-text-sm);
          color: var(--aiwari-fg-dim);
        }
        .aiwari-sirus-minimap .meta .clock {
          font-family: var(--aiwari-font-mono);
          letter-spacing: 0.02em;
        }
        .aiwari-sirus-minimap .meta .weather {
          color: var(--aiwari-gold-dark);
          display: inline-flex;
          align-items: center;
        }
        @media (pointer: coarse) {
          .aiwari-sirus-minimap {
            top: 8px;
            right: 8px;
            transform: scale(0.85);
            transform-origin: top right;
          }
        }
      `}</style>
    </div>
  );
}

/**
 * R232q86zdb (Wave 2 #1): React.memo wrapper.
 *
 * Minimap takes zero props. The canvas inside re-paints every frame via
 * requestAnimationFrame regardless of React renders — what we want to
 * guard is the React subtree (frame, label, tracking dropdown). With
 * `useSelfPlayer()` above instead of `useNet(s => s.players)`, the
 * component re-renders only when self moves zones / dies — not when any
 * remote player ticks. Memo seals it against parent re-renders too.
 */
export const Minimap = memo(MinimapImpl);
